import React, { useState } from'react';
import axios from'axios';
import Api from'../../services/api';
import { clearBookingState } from'../../services/bookingStatePersistence';

const RatingModal = ({ booking, driverName, onRatingComplete, onClose }) => {
 const [rating, setRating] = useState(0);
 const [hoverRating, setHoverRating] = useState(0);
 const [feedback, setFeedback] = useState('');
 const [loading, setLoading] = useState(false);
 const [error, setError] = useState(null);

 // Clear all booking cache from localStorage
 const clearBookingCache = () => {
 try {
 localStorage.removeItem('current_booking_data');
 localStorage.removeItem('trip_started_booking');
 clearBookingState();
 console.log('🗑️ Booking cache cleared from localStorage');
 } catch (error) {
 console.error('❌ Error clearing booking cache:', error);
 }
 };

 const handleSubmit = async () => {
 if (rating === 0) {
 setError('Please select a rating');
 return;
 }

 setLoading(true);
 setError(null);

 try {
 const token = localStorage.getItem('uttaapp_token');
 const bookingId = booking._id || booking.id;

 const response = await axios.post(
 Api.Rating.submitDriverRating,
 {
 bookingId: bookingId,
 rating: rating,
 feedback: feedback.trim() || null
 },
 {
 headers: {'Authorization':`Bearer ${token}`,'Content-Type':'application/json'}
 }
 );

 if (response.data.success) {
 console.log('✅ Rating submitted successfully');
 setLoading(false);
 // Clear booking cache after successful rating submission
 clearBookingCache();
 // Call onRatingComplete immediately to trigger navigation
 if (onRatingComplete) {
 onRatingComplete(response.data.rating);
 }
 }
 } catch (error) {
 console.error('Error submitting rating:', error);
 setError(error.response?.data?.message ||'Failed to submit rating. Please try again.');
 setLoading(false);
 }
 };

 const StarIcon = ({ filled, hovered, onClick, onMouseEnter, onMouseLeave }) => (
 <svg
 className={`w-10 h-10 cursor-pointer transition-all duration-200 ${
 filled || hovered
 ?'text-yellow-400 fill-current transform scale-110':'text-gray-300 fill-none'}`}
 onClick={onClick}
 onMouseEnter={onMouseEnter}
 onMouseLeave={onMouseLeave}
 viewBox="0 0 24 24"stroke="currentColor"strokeWidth={1}
 >
 <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
 </svg>
 );

 return (
 <div className="fixed inset-0 z-[10010] flex items-center justify-center bg-black bg-opacity-50 p-0 sm:p-4"style={{ display:'flex'}}>
 <div className="md:hidden fixed inset-x-0 bottom-0 z-[10011] w-full max-h-[92dvh] overflow-y-auto overscroll-contain rounded-t-[30px] border-t border-white/25 bg-[linear-gradient(180deg,#f8fbff_0%,#eef4ff_35%,#ffffff_100%)] pb-safe shadow-[0_-24px_80px_-38px_rgba(15,23,42,0.55)] animate-slideUp">
 <div className="sticky top-0 z-10 rounded-t-[30px] border-b border-slate-200/80 bg-white/95 px-4 pb-3 pt-2 backdrop-blur-xl">
 <div className="mx-auto mb-2 h-1.5 w-14 rounded-full bg-slate-300"/>
 <div className="flex items-center justify-between gap-3">
 <h2 className="text-[1.06rem] font-black tracking-[-0.02em] text-slate-900">Rate Your Driver</h2>
 <button
 onClick={() => {
 clearBookingCache();
 if (onClose) onClose();
 }}
 className="rounded-xl border border-slate-200 bg-slate-50 p-2 text-slate-600 transition-colors hover:bg-slate-100 hover:text-slate-900"disabled={loading}
 aria-label="Close rating modal">
 <svg className="h-5 w-5"fill="none"stroke="currentColor"viewBox="0 0 24 24">
 <path strokeLinecap="round"strokeLinejoin="round"strokeWidth={2} d="M6 18L18 6M6 6l12 12"/>
 </svg>
 </button>
 </div>
 </div>

 <div className="px-4 pb-5 pt-4">
 <div className="rounded-[22px] border border-slate-200/90 bg-white p-4 shadow-[0_24px_56px_-38px_rgba(15,23,42,0.32)]">
 <div className="rounded-[16px] border border-indigo-200 bg-[linear-gradient(135deg,#eef2ff_0%,#f8fbff_100%)] p-3.5">
 <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-indigo-500">Driver</p>
 <p className="mt-1 text-lg font-black text-slate-900">{driverName ||'Driver'}</p>
 </div>

 {error && (
 <div className="mt-3 rounded-[14px] border border-rose-200 bg-rose-50 px-3 py-2.5">
 <p className="text-sm font-semibold text-rose-700">{error}</p>
 </div>
 )}

 <div className="mt-4 rounded-[18px] border border-slate-200 bg-slate-50 px-3.5 py-3.5">
 <label className="block text-[13px] font-black text-slate-800 mb-3">
 How was your experience?
 </label>
 <div className="flex items-center justify-center gap-1.5">
 {[1, 2, 3, 4, 5].map((star) => (
 <button
 key={star}
 type="button"onClick={() => setRating(star)}
 onMouseEnter={() => setHoverRating(star)}
 onMouseLeave={() => setHoverRating(0)}
 className={`flex h-12 w-12 items-center justify-center rounded-2xl border transition-all ${
 star <= (hoverRating || rating)
 ?'border-amber-300 bg-amber-50':'border-slate-200 bg-white'}`}
 disabled={loading}
 aria-label={`Rate ${star} star${star > 1 ?'s':''}`}
 >
 <svg
 className={`h-7 w-7 ${
 star <= (hoverRating || rating)
 ?'text-amber-500 fill-current':'text-slate-300 fill-none'}`}
 viewBox="0 0 24 24"stroke="currentColor"strokeWidth={1.2}
 >
 <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
 </svg>
 </button>
 ))}
 </div>

 {rating > 0 && (
 <p className="mt-3 text-center text-sm font-semibold text-slate-600">
 {rating === 1 &&'Poor'}
 {rating === 2 &&'Fair'}
 {rating === 3 &&'Good'}
 {rating === 4 &&'Very Good'}
 {rating === 5 &&'Excellent'}
 </p>
 )}
 </div>

 <div className="mt-4">
 <label className="block text-[12px] font-bold uppercase tracking-[0.14em] text-slate-500 mb-2">
 Share your feedback (optional)
 </label>
 <textarea
 value={feedback}
 onChange={(e) => setFeedback(e.target.value)}
 placeholder="Tell us about your experience..."className="w-full resize-none rounded-[16px] border border-slate-200 bg-white px-3.5 py-3 text-sm text-slate-800 placeholder:text-slate-400 focus:border-indigo-300 focus:ring-2 focus:ring-indigo-100"rows="4"disabled={loading}
 />
 </div>

 <div className="mt-4 grid grid-cols-2 gap-2.5">
 <button
 onClick={() => {
 clearBookingCache();
 if (onClose) onClose();
 }}
 className="rounded-[14px] border border-slate-300 bg-white px-4 py-3 text-sm font-bold text-slate-700 transition-colors hover:bg-slate-50"disabled={loading}
 >
 Skip
 </button>
 <button
 onClick={handleSubmit}
 disabled={loading || rating === 0}
 className={`rounded-[14px] px-4 py-3 text-sm font-bold text-white transition-all ${
 rating === 0
 ?'bg-slate-300 cursor-not-allowed':'bg-[linear-gradient(135deg,#f59e0b_0%,#f97316_100%)] shadow-[0_14px_26px_-18px_rgba(249,115,22,0.8)]'}`}
 >
 {loading ?'Submitting...':'Submit Rating'}
 </button>
 </div>
 </div>
 </div>
 </div>

 <div className="hidden md:block bg-white rounded-2xl shadow-2xl w-full max-w-md lg:max-w-lg xl:max-w-xl max-h-[min(90dvh,90vh)] overflow-y-auto overscroll-contain animate-slideUp">
 {/* Header */}
 <div className="bg-gradient-to-r from-yellow-400 via-amber-500 to-yellow-500 p-6">
 <div className="flex items-center justify-between mb-4">
 <h2 className="text-2xl font-bold text-white">Rate Your Driver</h2>
 <button
 onClick={() => {
 // Clear booking cache when closing
 clearBookingCache();
 if (onClose) onClose();
 }}
 className="text-white hover:text-gray-200 transition-colors"disabled={loading}
 >
 <svg className="w-6 h-6"fill="none"stroke="currentColor"viewBox="0 0 24 24">
 <path strokeLinecap="round"strokeLinejoin="round"strokeWidth={2} d="M6 18L18 6M6 6l12 12"/>
 </svg>
 </button>
 </div>
 <div className="bg-white/20 backdrop-blur-sm rounded-lg p-4">
 <p className="text-white text-sm mb-1">Driver</p>
 <p className="text-white text-xl font-bold">{driverName ||'Driver'}</p>
 </div>
 </div>

 {/* Content */}
 <div className="p-6">
 {/* Error Message */}
 {error && (
 <div className="mb-6 bg-red-50 border-l-4 border-red-500 p-4 rounded-lg">
 <p className="text-red-700 text-sm font-semibold">{error}</p>
 </div>
 )}

 {/* Rating Stars */}
 <div className="mb-6">
 <label className="block text-gray-700 font-semibold mb-4 text-lg">
 How was your experience?
 </label>
 <div className="flex justify-center space-x-2">
 {[1, 2, 3, 4, 5].map((star) => (
 <StarIcon
 key={star}
 filled={star <= rating}
 hovered={star <= hoverRating}
 onClick={() => setRating(star)}
 onMouseEnter={() => setHoverRating(star)}
 onMouseLeave={() => setHoverRating(0)}
 />
 ))}
 </div>
 {rating > 0 && (
 <p className="text-center mt-4 text-gray-600 font-medium">
 {rating === 1 &&'Poor'}
 {rating === 2 &&'Fair'}
 {rating === 3 &&'Good'}
 {rating === 4 &&'Very Good'}
 {rating === 5 &&'Excellent'}
 </p>
 )}
 </div>

 {/* Feedback */}
 <div className="mb-6">
 <label className="block text-gray-700 font-semibold mb-2">
 Share your feedback (optional)
 </label>
 <textarea
 value={feedback}
 onChange={(e) => setFeedback(e.target.value)}
 placeholder="Tell us about your experience..."className="w-full p-4 border-2 border-gray-200 rounded-xl focus:border-amber-400 focus:ring-2 focus:ring-amber-200 transition-all resize-none"rows="4"disabled={loading}
 />
 </div>

 {/* Action Buttons */}
 <div className="flex gap-4">
 <button
 onClick={() => {
 // Clear booking cache when skipping
 clearBookingCache();
 if (onClose) onClose();
 }}
 className="flex-1 px-6 py-4 bg-gray-100 hover:bg-gray-200 text-gray-800 rounded-xl font-bold text-lg transition-all duration-200"disabled={loading}
 >
 Skip
 </button>
 <button
 onClick={handleSubmit}
 disabled={loading || rating === 0}
 className={`flex-1 px-6 py-4 rounded-xl font-bold text-lg text-white transition-all transform ${
 rating === 0
 ?'bg-gray-300 cursor-not-allowed':'bg-gradient-to-r from-amber-500 to-yellow-600 hover:from-amber-600 hover:to-yellow-700 hover:scale-105 shadow-lg hover:shadow-xl'}`}
 >
 {loading ? (
 <div className="flex items-center justify-center space-x-2">
 <svg className="animate-spin w-5 h-5"fill="none"viewBox="0 0 24 24">
 <circle className="opacity-25"cx="12"cy="12"r="10"stroke="currentColor"strokeWidth="4"></circle>
 <path className="opacity-75"fill="currentColor"d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
 </svg>
 <span>Submitting...</span>
 </div>
 ) : ('Submit Rating')}
 </button>
 </div>
 </div>
 </div>
 </div>
 );
};

export default RatingModal;

